import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import axios from "../api/axios";
import StatusTimeline from "../components/StatusTimeline";

const timelineSteps = [
    { key: "Anfrage", label: "Anfrage wird bearbeitet", hint: "Termine abstimmen" },
    { key: "Angebot", label: "Angebot wird erstellt", hint: "Termine abstimmen" },
    { key: "AngebotAngenommen", label: "Angebot angenommen", hint: "Termine abstimmen" },
    { key: "Bestellung", label: "Material ist bestellt", hint: "Auftrag bestätigt" },
    { key: "Terminanfrage", label: "Terminanfrage", hint: "Fertigung & Zuschnitt" },
    { key: "Terminiert", label: "Terminiert", hint: "Einbau oder Abholung" },
];

const formatDateTime = (value) => {
    if (!value) return "—";
    return new Date(value).toLocaleString("de-DE", {
        day: "2-digit",
        month: "2-digit",
        year: "numeric",
        hour: "2-digit",
        minute: "2-digit",
        hour12: false,
    }).replace(",", " um") + " Uhr";
};

const formatDate = (value) => {
    if (!value) return "—";
    return new Date(value).toLocaleDateString("de-DE", {
        day: "2-digit",
        month: "2-digit",
        year: "numeric",
    });
};

const formatOptionDate = (value) =>
    new Date(`${value}T12:00:00`).toLocaleDateString("de-DE", {
        weekday: "long",
        day: "2-digit",
        month: "2-digit",
        year: "numeric",
    });

const errorMessage = (error, fallback) =>
    typeof error?.response?.data === "string" && error.response.data ? error.response.data : fallback;

const Status = () => {
    const queryClient = useQueryClient();
    const { trackingId } = useParams();
    const [feedback, setFeedback] = useState("");
    const [bookingOptions, setBookingOptions] = useState([]);
    const [bookingMessage, setBookingMessage] = useState(null);
    const [bookedAppointment, setBookedAppointment] = useState(null);

    const fetchProject = async () => {
        const response = await axios.get("/projects/projectstatus", {
            params: { id: trackingId },
        });
        return response.data;
    };

    const { data, isLoading, isError } = useQuery({
        queryKey: ["project", trackingId],
        queryFn: fetchProject,
    });

    const mutationAccept = useMutation({
        mutationFn: (appointmentId) => axios.put(`/status/${appointmentId}/accept`),
        onSuccess: () => queryClient.invalidateQueries({ queryKey: ["project", trackingId] }),
    });

    const mutationDecline = useMutation({
        mutationFn: (appointmentId) => axios.put(`/status/${appointmentId}/decline`),
        onSuccess: () => queryClient.invalidateQueries({ queryKey: ["project", trackingId] }),
    });

    const feedbackMutation = useMutation({
        mutationFn: (nextFeedback) =>
            axios.put("/projects/projectstatus/feedback", { feedback: nextFeedback }, { params: { id: trackingId } }),
        onSuccess: () => queryClient.invalidateQueries({ queryKey: ["project", trackingId] }),
    });

    const onAccept = (appointmentId) => mutationAccept.mutate(appointmentId);
    const onDecline = (appointmentId) => mutationDecline.mutate(appointmentId);

    const optionsMutation = useMutation({
        mutationFn: async (after) => {
            const response = await axios.post("/projects/projectstatus/appointment-options", null, {
                params: { id: trackingId, ...(after ? { after } : {}) },
            });
            return response.data;
        },
        onMutate: () => setBookingMessage(null),
        onSuccess: (options) => {
            setBookingOptions(options);
            if (options.length === 0) {
                setBookingMessage({ type: "info", text: "Aktuell konnten keine freien Termine gefunden werden. Bitte versuchen Sie es später erneut oder kontaktieren Sie uns." });
            }
        },
        onError: (error) => setBookingMessage({ type: "error", text: errorMessage(error, "Terminvorschläge konnten nicht geladen werden.") }),
    });

    const bookMutation = useMutation({
        mutationFn: async (option) => {
            const response = await axios.post("/projects/projectstatus/appointment-booking",
                { date: option.date, startTime: option.startTime },
                { params: { id: trackingId } });
            return response.data;
        },
        onMutate: () => setBookingMessage(null),
        onSuccess: (appointment) => {
            setBookedAppointment(appointment);
            setBookingOptions([]);
            queryClient.invalidateQueries({ queryKey: ["project", trackingId] });
        },
        onError: (error) => {
            setBookingOptions([]);
            setBookingMessage({ type: "error", text: errorMessage(error, "Der Termin konnte nicht gebucht werden.") });
            queryClient.invalidateQueries({ queryKey: ["project", trackingId] });
        },
    });

    const isBookingBusy = optionsMutation.isPending || bookMutation.isPending;

    const shouldShowTerminanfrageStep =
        (data?.projectStatus || "") === "Terminanfrage/Terminierung" &&
        ((data?.projectPhase || "").toLowerCase() === "montage");

    const timelineStepsToRender = shouldShowTerminanfrageStep
        ? timelineSteps
        : timelineSteps.filter((step) => step.key !== "Terminanfrage");

    useEffect(() => {
        setFeedback(data?.feedback || "");
    }, [data?.feedback]);

    if (isLoading) {
        return (
            <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-slate-50 via-white to-blue-50 text-slate-600">
                Lade Ihren Auftragsstatus ...
            </div>
        );
    }

    if (isError || !data) {
        return (
            <div className="min-h-screen flex items-center justify-center bg-slate-50 text-red-600">
                Unter der Tracking-URL konnte kein Auftrag gefunden werden. Bitte überprüfen Sie die URL oder kontaktieren Sie uns, falls Sie Hilfe benötigen.
            </div>
        );
    }

    const openAppointments = (data.appointments || [])
        .filter((appointment) => appointment.confirmed === false && (!data.activeAppointment || appointment.type === data.activeAppointment))
        .sort((a, b) => {
            const dateA = new Date(a.date);
            const dateB = new Date(b.date);

            if (dateA - dateB !== 0) {
                return dateA - dateB;
            }
            return a.startTime.localeCompare(b.startTime);
        });

    const confirmedAppointments = (data.appointments || [])
        .filter((appointment) => appointment.confirmed === true)
        .sort((a, b) => {
            const dateA = new Date(a.date);
            const dateB = new Date(b.date);

            if (dateA - dateB !== 0) {
                return dateA - dateB;
            }
            return (a.startTime || "").localeCompare(b.startTime || "");
        });

    const customer = data.customer || {};
    const activeStepKey = data.timeLineStatus || "Anfrage";
    const hasSubmittedFeedback = (data?.feedback || "").trim().length > 0;

    return (
        <div className="min-h-screen bg-gradient-to-br from-slate-50 via-white to-blue-50 text-slate-900">
            <div className="max-w-6xl mx-auto px-4 py-12 space-y-8">
                <header className="bg-white/80 backdrop-blur rounded-3xl shadow-sm border border-slate-100 p-6 flex flex-col gap-4">
                    <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
                        <div className="flex items-start gap-3">
                            <div>
                                <p className="text-sm text-slate-500">Bestellstatus</p>
                                <h1 className="text-2xl font-semibold text-slate-900">{data.name || "Ihr Auftrag"}</h1>
                            </div>
                        </div>
                        <div className="flex flex-col items-start sm:items-end gap-2">
                            <span className="inline-flex items-center px-3 py-1 rounded-full bg-blue-50 text-blue-700 text-sm font-semibold">
                                {/* {data.projectStatus || "In Bearbeitung"} */}
                            </span>
                        </div>
                    </div>

                    <div className="grid grid-cols-1 sm:grid-cols-4 gap-4">
                        <div className="rounded-2xl border border-slate-100 bg-slate-50/60 p-4">
                            <p className="text-xs uppercase tracking-wide text-slate-500">Bestellt am</p>
                            <p className="text-sm font-semibold text-slate-900">{formatDateTime(data.createdAt)}</p>
                        </div>
                        <div className="rounded-2xl border border-slate-100 bg-slate-50/60 p-4">
                            <p className="text-xs uppercase tracking-wide text-slate-500">Kundennummer</p>
                            <p className="text-sm font-semibold text-slate-900">{customer.customerNumber || "—"}</p>
                        </div>
                        <div className="rounded-2xl border border-slate-100 bg-slate-50/60 p-4">
                            <p className="text-xs uppercase tracking-wide text-slate-500">Projektnummer</p>
                            <p className="text-sm font-semibold text-slate-900">{data.projectNumber || "—"}</p>
                        </div>
                        <div className="rounded-2xl border border-slate-100 bg-slate-50/60 p-4">
                            <p className="text-xs uppercase tracking-wide text-slate-500">Aktuelle Phase</p>
                            <p className="text-sm font-semibold text-slate-900">{data.projectPhase || activeStepKey}</p>
                        </div>
                    </div>
                </header>

                <div className="grid grid-cols-1 lg:grid-cols-12 gap-6">
                    <section className="lg:col-span-12 bg-white rounded-3xl border border-slate-100 shadow-sm p-6 md:p-8">
                        <div className="flex items-center justify-between mb-4">
                            <h2 className="text-lg font-semibold text-slate-900">Fortschritt</h2>
                            <span className="text-xs text-slate-500">{timelineStepsToRender.length} Schritte</span>
                        </div>
                        <StatusTimeline currentStatus={activeStepKey} steps={timelineStepsToRender} />
                    </section>

                    {(data.appointmentBookingEnabled || bookedAppointment || bookingMessage) && (
                        <section className="lg:col-span-12 bg-white rounded-3xl border border-blue-100 shadow-sm p-6 space-y-4">
                            <div className="flex items-center justify-between gap-3 flex-wrap">
                                <div>
                                    <h2 className="text-lg font-semibold text-slate-900">Terminvereinbarung</h2>
                                    {data.appointmentBookingType && !bookedAppointment && (
                                        <p className="text-xs text-slate-500">Terminart: {data.appointmentBookingType}</p>
                                    )}
                                </div>
                                {!bookedAppointment && data.appointmentBookingEnabled && bookingOptions.length === 0 && (
                                    <button
                                        type="button"
                                        onClick={() => optionsMutation.mutate(null)}
                                        disabled={isBookingBusy}
                                        className="px-4 py-2 rounded-xl bg-blue-600 text-white text-sm font-semibold hover:bg-blue-700 transition disabled:opacity-60"
                                    >
                                        Terminvorschläge anfordern
                                    </button>
                                )}
                            </div>

                            {bookedAppointment ? (
                                <div className="rounded-2xl border border-emerald-200 bg-emerald-50 p-4 text-sm text-emerald-800">
                                    Vielen Dank! Ihr Termin am <strong>{formatOptionDate(bookedAppointment.date)}</strong> um{" "}
                                    <strong>{bookedAppointment.startTime?.substring(0, 5)} Uhr</strong> ist verbindlich bestätigt.
                                </div>
                            ) : (
                                <>
                                    {bookingOptions.length === 0 && !isBookingBusy && !bookingMessage && (
                                        <p className="text-sm text-slate-600">
                                            Ihr Auftrag ist bereit zur Terminvereinbarung. Fordern Sie Terminvorschläge an und bestätigen Sie
                                            direkt den Termin, der Ihnen am besten passt.
                                        </p>
                                    )}

                                    {isBookingBusy && (
                                        <div role="status" aria-live="polite" className="flex items-center gap-2 rounded-2xl bg-blue-50 px-4 py-3 text-sm text-blue-800">
                                            <svg className="h-4 w-4 animate-spin" viewBox="0 0 24 24" fill="none" aria-hidden="true">
                                                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                                                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
                                            </svg>
                                            {bookMutation.isPending
                                                ? "Ihr Termin wird gebucht …"
                                                : "Wir suchen passende Termine für Sie. Das kann einen Moment dauern …"}
                                        </div>
                                    )}

                                    {bookingMessage && (
                                        <div className={`rounded-2xl p-4 text-sm ${bookingMessage.type === "error" ? "bg-red-50 text-red-700 border border-red-100" : "bg-slate-50 text-slate-600 border border-slate-100"}`}>
                                            {bookingMessage.text}
                                        </div>
                                    )}

                                    {bookingOptions.length > 0 && !isBookingBusy && (
                                        <div className="space-y-3">
                                            {bookingOptions.map((option) => (
                                                <div
                                                    key={`${option.date}-${option.startTime}`}
                                                    className="rounded-2xl border border-slate-100 bg-slate-50/60 p-4 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3"
                                                >
                                                    <div>
                                                        <p className="text-sm font-semibold text-slate-900">{formatOptionDate(option.date)}</p>
                                                        <p className="text-sm text-slate-600">
                                                            {option.startTime?.substring(0, 5)} – {option.endTime?.substring(0, 5)} Uhr
                                                        </p>
                                                    </div>
                                                    <button
                                                        type="button"
                                                        onClick={() => bookMutation.mutate(option)}
                                                        className="px-4 py-2 rounded-xl bg-green-600 text-white text-sm font-semibold hover:bg-green-700 transition"
                                                    >
                                                        Diesen Termin bestätigen
                                                    </button>
                                                </div>
                                            ))}
                                            <button
                                                type="button"
                                                onClick={() => optionsMutation.mutate(bookingOptions[bookingOptions.length - 1].date)}
                                                className="text-sm font-medium text-blue-700 hover:underline"
                                            >
                                                Keiner passt? Spätere Termine anzeigen
                                            </button>
                                        </div>
                                    )}
                                </>
                            )}
                        </section>
                    )}

                    <section className="lg:col-span-7 bg-white rounded-3xl border border-slate-100 shadow-sm p-6">
                        <div className="flex items-center justify-between mb-4">
                            <h2 className="text-lg font-semibold text-slate-900">Lieferadresse</h2>

                        </div>
                        <div className="space-y-2 text-sm text-slate-700">
                            <p className="font-semibold text-slate-900">{customer.name}</p>
                            <p>{customer.street} {customer.houseNumber}</p>
                            <p>{customer.postalCode} {customer.city}</p>
                            {customer.phone && <p className="text-slate-500">Tel: {customer.phone}</p>}
                            {customer.email && <p className="text-slate-500">E-Mail: {customer.email}</p>}
                        </div>
                    </section>

                    <section className="lg:col-span-5 bg-white rounded-3xl border border-slate-100 shadow-sm p-6 space-y-4">
                        <div className="flex items-center justify-between">
                            <h2 className="text-lg font-semibold text-slate-900">Termine wählen</h2>
                            <span className="text-xs text-slate-500">
                                {data.activeAppointment ? `Kategorie: ${data.activeAppointment}` : "Offene Termine"}
                            </span>
                        </div>

                        {openAppointments.length === 0 ? (
                            <div className="rounded-2xl border border-dashed border-slate-200 p-4 text-sm text-slate-500">
                                Aktuell liegen keine offenen Terminvorschläge vor. Sobald neue Termine verfügbar sind, informieren wir Sie per E-Mail.
                            </div>
                        ) : (
                            <div className="space-y-3">
                                {openAppointments.map((appointment) => (
                                    <div
                                        key={appointment.id}
                                        className={`rounded-2xl border p-4 flex flex-col gap-3 transition shadow-sm ${appointment.declined ? "border-red-100 bg-red-50/40" : "border-slate-100 bg-slate-50/60"}`}
                                    >
                                        <div className="flex items-center justify-between">
                                            <div>
                                                <p className="text-sm font-semibold text-slate-900">
                                                    {formatDate(appointment.date)} · {appointment.startTime?.substring(0, 5)} Uhr
                                                </p>
                                                <p className="text-xs text-slate-500">{appointment.type}</p>
                                            </div>
                                            <div className="flex gap-2">
                                                <button
                                                    className="px-3 py-2 rounded-xl bg-green-600 text-white text-sm font-semibold hover:bg-green-700 transition"
                                                    onClick={() => onAccept(appointment.id)}
                                                >
                                                    Annehmen
                                                </button>
                                                <button
                                                    className="px-3 py-2 rounded-xl bg-red-500 text-white text-sm font-semibold hover:bg-red-600 transition"
                                                    onClick={() => onDecline(appointment.id)}
                                                >
                                                    Ablehnen
                                                </button>
                                            </div>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        )}
                    </section>

                    <section className="lg:col-span-12 bg-white rounded-3xl border border-slate-100 shadow-sm p-6 space-y-4">
                        <div className="flex items-center justify-between">
                            <h2 className="text-lg font-semibold text-slate-900">Bestätigte Termine</h2>
                            <span className="text-xs text-slate-500">{confirmedAppointments.length}</span>
                        </div>

                        {confirmedAppointments.length === 0 ? (
                            <div className="rounded-2xl border border-dashed border-slate-200 p-4 text-sm text-slate-500">
                                Es wurden noch keine Termine bestätigt.
                            </div>
                        ) : (
                            <div className="space-y-3">
                                {confirmedAppointments.map((appointment) => (
                                    <div
                                        key={appointment.id}
                                        className="rounded-2xl border border-emerald-100 bg-emerald-50/40 p-4"
                                    >
                                        <p className="text-sm font-semibold text-slate-900">
                                            {formatDate(appointment.date)} · {appointment.startTime?.substring(0, 5)} Uhr
                                        </p>
                                        <p className="text-xs text-slate-500 mt-1">{appointment.type}</p>
                                    </div>
                                ))}
                            </div>
                        )}
                    </section>

                    {!hasSubmittedFeedback && (
                        <section className="lg:col-span-12 bg-white rounded-3xl border border-slate-100 shadow-sm p-6 space-y-4">
                            <div className="flex items-center justify-between">
                                <h2 className="text-lg font-semibold text-slate-900">Feedback</h2>
                            </div>

                            <textarea
                                value={feedback}
                                onChange={(event) => setFeedback(event.target.value)}
                                rows={4}
                                placeholder="Wie zufrieden waren Sie mit dem Ablauf?"
                                className="w-full rounded-2xl border border-slate-200 px-4 py-3 text-sm text-slate-900 focus:outline-none focus:ring-2 focus:ring-blue-200"
                            />

                            <div className="flex justify-end">
                                <button
                                    type="button"
                                    onClick={() => feedbackMutation.mutate(feedback)}
                                    disabled={feedbackMutation.isPending}
                                    className="px-4 py-2 rounded-xl bg-blue-600 text-white text-sm font-semibold hover:bg-blue-700 transition disabled:opacity-60"
                                >
                                    {feedbackMutation.isPending ? "Speichert..." : "Feedback speichern"}
                                </button>
                            </div>
                        </section>
                    )}
                </div>

            </div>
        </div>
    );
};

export default Status;
